import React, { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Sparkles,
  Calendar,
  Clock,
  ArrowRight,
  ArrowLeft,
  User,
} from 'lucide-react';
import { findPostBySlug, posts } from '../data/blogPosts';
import { setMetaTags } from '../utils/seo';

/**
 * Render a single content block to JSX. Keep this small and stable —
 * if we add new block types, extend the switch.
 */
const Block = ({ block }) => {
  switch (block.type) {
    case 'h2':
      return (
        <h2
          className="text-2xl sm:text-3xl font-bold text-slate-100 mt-12 mb-3"
          style={{ fontFamily: 'Manrope, sans-serif' }}
        >
          {block.text}
        </h2>
      );
    case 'h3':
      return (
        <h3
          className="text-lg sm:text-xl font-semibold text-slate-100 mt-7 mb-2"
          style={{ fontFamily: 'Manrope, sans-serif' }}
        >
          {block.text}
        </h3>
      );
    case 'ul':
      return (
        <ul className="list-disc pl-6 text-slate-300 space-y-2 my-5 leading-relaxed">
          {block.items.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      );
    case 'ol':
      return (
        <ol className="list-decimal pl-6 text-slate-300 space-y-2 my-5 leading-relaxed">
          {block.items.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ol>
      );
    case 'quote':
      return (
        <blockquote className="border-l-4 border-teal-500/60 pl-5 my-7 italic text-slate-200 text-lg leading-relaxed">
          {block.text}
        </blockquote>
      );
    case 'cta':
      return (
        <div className="my-8">
          <Link to={block.href}>
            <Button
              size="lg"
              className="bg-gradient-to-r from-teal-400 to-emerald-500 text-slate-900 hover:opacity-90 font-semibold"
            >
              {block.text}
            </Button>
          </Link>
        </div>
      );
    case 'p':
    default:
      return (
        <p className="text-slate-300 leading-relaxed text-base mb-5">
          {block.text}
        </p>
      );
  }
};

const BlogPost = () => {
  const { slug } = useParams();
  const post = findPostBySlug(slug);

  useEffect(() => {
    if (!post) return;
    setMetaTags({
      title: `${post.title} — InterviewWala blog`,
      description: post.excerpt,
      url: `/blog/${post.slug}`,
      image: post.cover,
      type: 'article',
    });
    if (post.keywords) {
      const kw = document.querySelector('meta[name="keywords"]');
      if (kw) kw.setAttribute('content', post.keywords);
    }
    window.scrollTo({ top: 0 });
  }, [post]);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  // Build a simple list of "related" posts (everything except current).
  const related = posts.filter((p) => p.slug !== post.slug).slice(0, 2);

  return (
    <div
      className="min-h-screen bg-slate-950 text-slate-100"
      data-testid={`blog-post-${post.slug}`}
    >
      {/* Nav */}
      <nav className="fixed top-0 inset-x-0 z-30 backdrop-blur-md bg-slate-950/60 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-slate-900" />
            </div>
            <span
              className="text-lg font-semibold"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              InterviewWala
            </span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-4">
            <Link
              to="/pricing"
              className="text-sm text-slate-300 hover:text-white transition px-3 py-2"
            >
              Pricing
            </Link>
            <Link
              to="/blog"
              className="text-sm text-white font-medium px-3 py-2"
            >
              Blog
            </Link>
            <Link
              to="/login"
              className="text-sm text-slate-300 hover:text-white transition px-3 py-2"
            >
              Sign in
            </Link>
            <Link to="/signup">
              <Button
                size="sm"
                className="bg-gradient-to-r from-teal-400 to-emerald-500 text-slate-900 hover:opacity-90 font-semibold"
              >
                Start free
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <article className="pt-28 pb-20 px-6 lg:px-10">
        <div className="max-w-3xl mx-auto">
          <Link
            to="/blog"
            className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-teal-400 mb-6"
            data-testid="blog-back-link"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> All articles
          </Link>

          <header className="mb-10">
            <div className="flex items-center gap-3 text-[11px] text-slate-400 mb-3">
              <span className="inline-flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {new Date(post.date).toLocaleDateString(undefined, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })}
              </span>
              <span>·</span>
              <span className="inline-flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {post.readTime}
              </span>
              <span>·</span>
              <span className="inline-flex items-center gap-1">
                <User className="w-3 h-3" />
                {post.author}
              </span>
            </div>
            <h1
              className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-4"
              style={{ fontFamily: 'Manrope, sans-serif' }}
              data-testid="blog-post-title"
            >
              {post.title}
            </h1>
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
              {post.excerpt}
            </p>
          </header>

          {post.cover && (
            <div className="rounded-xl overflow-hidden border border-slate-800 mb-10">
              <img
                src={post.cover}
                alt=""
                className="w-full aspect-[16/9] object-cover object-top"
              />
            </div>
          )}

          <div className="prose prose-invert max-w-none">
            {post.content.map((block, i) => (
              <Block key={i} block={block} />
            ))}
          </div>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <div className="max-w-3xl mx-auto mt-20 pt-10 border-t border-slate-800">
            <p className="text-xs uppercase tracking-wider text-teal-400 font-bold mb-5">
              Keep reading
            </p>
            <ul className="space-y-3">
              {related.map((p) => (
                <li key={p.slug}>
                  <Link
                    to={`/blog/${p.slug}`}
                    className="block rounded-lg border border-slate-800 bg-slate-900/40 hover:border-teal-500/40 hover:bg-slate-900/70 transition px-5 py-4"
                    data-testid={`blog-related-${p.slug}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-100 truncate">
                          {p.title}
                        </p>
                        <p className="text-xs text-slate-400 truncate mt-0.5">
                          {p.excerpt}
                        </p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-teal-400 flex-shrink-0" />
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </article>
    </div>
  );
};

export default BlogPost;
